import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { omit } from "es-toolkit";
import { Euro } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { validationErrors } from "@/helpers";
import {
	formatPriceCentsInput,
	formatPriceCentsInputValue,
} from "@/helpers/price";
import { updateStore } from "../../api";
import {
	Badge,
	CountrySelect,
	FormContainer,
	FormSection,
	InputColor,
	InputText,
	InputTextarea,
} from "../../components";
import { z } from "../../constants";
import { useCurrentStore } from "../../hooks";

const formSchema = z.object({
	address1: z.string().nullable().optional(),
	address2: z.string().nullable().optional(),
	zipcode: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	websiteUrl: z.string().nullable().optional(),
	phoneNumber: z.string().nullable().optional(),
	emailAddress: z.string().nullable().optional(),
	color: z.string().nullable().optional(),
	openaiApiKey: z.string().nullable().optional(),
	aiPrompt: z.string().nullable().optional(),
	countryId: z.string(),
	key: z.string(),
	name: z.string(),
	legalName: z.string().nullable().optional(),
	legalForm: z.string().nullable().optional(),
	shareCapitalCents: z.number().nullable().optional(),
	siret: z.string().nullable().optional(),
	vatNumber: z.string().nullable().optional(),
	rcs: z.string().nullable().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

const Store = () => {
	const store = useCurrentStore();
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: store?.name || t("store.pageTitle") }]);
	const queryClient = useQueryClient();
	const toastId = useRef<string>(null);
	const { control, handleSubmit, reset, setError } = useForm<FormSchema>({
		resolver: zodResolver(formSchema),
	});

	const { mutate, isPending } = useMutation({
		mutationFn: (data: FormSchema) => updateStore(store?.id, data),
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onSuccess: () => {
			toast.success(t("global.saved"), { id: toastId?.current || undefined });
			queryClient.invalidateQueries({ queryKey: ["stores"] });
		},
		onError: (error) => {
			// The API checks the SIRET and VAT number digits; show what it refused
			// on the field it refused it for.
			for (const [field, message] of Object.entries(validationErrors(error))) {
				setError(field as keyof FormSchema, { type: "server", message });
			}
			toast.error(t("global.savingError"), {
				id: toastId?.current || undefined,
			});
		},
	});

	const onSubmit = useCallback(
		(data: FormSchema) => {
			// The API never returns the OpenAI key, so the field always starts empty.
			// Sending it blank would erase the configured key, so only submit it when
			// the user actually typed a new one.
			mutate(data.openaiApiKey ? data : omit(data, ["openaiApiKey"]));
		},
		[mutate],
	);

	useEffect(() => {
		if (store) {
			reset({
				...store,
				countryId: store.country.id,
				// Write-only: never prefilled, cleared again after each save.
				openaiApiKey: "",
			});
		}
	}, [store, reset]);

	return (
		<FormContainer
			isLoading={isPending}
			onSubmit={handleSubmit(onSubmit)}
			title={t("store.pageTitle")}
			subtitle={t("store.pageSubtitle")}
		>
			<FormSection title={t("store.generalInformations")}>
				<Controller
					control={control}
					name="name"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("store.namePlaceholder")}
							type="text"
							label={t("store.nameLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="phoneNumber"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.phoneNumberPlaceholder")}
							type="text"
							label={t("store.phoneNumberLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="emailAddress"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.emailAddressPlaceholder")}
							type="text"
							label={t("store.emailAddressLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="websiteUrl"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.websiteUrlPlaceholder")}
							type="text"
							label={t("store.websiteUrlLabel")}
						/>
					)}
				/>
			</FormSection>
			<FormSection title={t("store.address")}>
				<Controller
					control={control}
					name="address1"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.address1Placeholder")}
							type="text"
							label={t("store.address1Label")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="address2"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.address2Placeholder")}
							type="text"
							label={t("store.address2Label")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="zipcode"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.zipcodePlaceholder")}
							type="text"
							label={t("store.zipcodeLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="city"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.cityPlaceholder")}
							type="text"
							label={t("store.cityLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="countryId"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<CountrySelect
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("store.countryPlaceholder")}
							label={t("store.countryLabel")}
						/>
					)}
				/>
			</FormSection>
			<FormSection title={t("store.legalIdentity")}>
				<Controller
					control={control}
					name="legalName"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.legalNamePlaceholder")}
							type="text"
							label={t("store.legalNameLabel")}
							hint={t("store.legalIdentityHint")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="legalForm"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.legalFormPlaceholder")}
							type="text"
							label={t("store.legalFormLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="shareCapitalCents"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							icon={<Euro />}
							step="0.01"
							type="number"
							onChange={(e) => onChange(formatPriceCentsInput(e))}
							value={formatPriceCentsInputValue(value)}
							placeholder={t("store.shareCapitalPlaceholder")}
							label={t("store.shareCapitalLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="siret"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.siretPlaceholder")}
							type="text"
							label={t("store.siretLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="vatNumber"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.vatNumberPlaceholder")}
							type="text"
							label={t("store.vatNumberLabel")}
						/>
					)}
				/>
				<Controller
					control={control}
					name="rcs"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.rcsPlaceholder")}
							type="text"
							label={t("store.rcsLabel")}
						/>
					)}
				/>
			</FormSection>
			<FormSection title={t("store.customization")}>
				<Controller
					control={control}
					name="color"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputColor
							colors={[
								"#ef4444",
								"#f97316",
								"#f59e0b",
								"#facc15",
								"#84cc16",
								"#10b981",
								"#14b8a6",
								"#22d3ee",
								"#38bdf8",
								"#60a5fa",
								"#4f46e5",
								"#a855f7",
								"#ec4899",
								"#f43f5e",
							]}
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							label={t("store.colorLabel")}
						/>
					)}
				/>
			</FormSection>
			<FormSection title={t("store.integrations")}>
				<div className="flex flex-col items-start gap-2">
					<Badge variant={store?.openaiConfigured ? "success" : "secondary"}>
						{store?.openaiConfigured
							? t("store.openaiApiKeyConfigured")
							: t("store.openaiApiKeyNotConfigured")}
					</Badge>
					<Controller
						control={control}
						name="openaiApiKey"
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<InputText
								error={error?.message}
								onChange={onChange}
								value={value || ""}
								placeholder={t("store.openaiApiKeyPlaceholder")}
								type="password"
								label={t("store.openaiApiKeyLabel")}
								hint={
									store?.openaiConfigured
										? t("store.openaiApiKeyReplaceHint")
										: t("store.openaiApiKeyHint")
								}
							/>
						)}
					/>
				</div>
				<Controller
					control={control}
					name="aiPrompt"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputTextarea
							error={error?.message}
							onChange={onChange}
							value={value || ""}
							placeholder={t("store.aiPromptPlaceholder")}
							label={t("store.aiPromptLabel")}
							hint={t("store.aiPromptHint")}
							rows={4}
						/>
					)}
				/>
			</FormSection>
		</FormContainer>
	);
};

export default Store;
